import React, { useContext, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NavigationPane from './navigation-pane/NavigationPane';
import styles from './navigation.scss';
import EnvVarsFeaturesContext from '../../scaffold/EnvVarsFeaturesContext';
import getCategories from '../../../client/fetch/categories';
import DesktopMenu from './desktop-menu/DesktopMenu';
import MobileMenu from './mobile-menu/MobileMenu';
import getNavigation from '../../../client/fetch/get-navigation';
import isMonetateGroup from '../../utils/monetate/isMonetateGroup';

export const fetchChildCategories = async ({
  categories,
  id,
  depth,
  isNavigationApi,
}) => {
  const childCategories = isNavigationApi
    ? await getNavigation({ id, depth })
    : await getCategories({
        id,
        depth,
      });
  return childCategories &&
    childCategories.child &&
    childCategories.child.length > 0
    ? {
        ...categories,
        child: categories.child.map(item =>
          item.id === id
            ? {
                ...item,
                child: [...childCategories.child],
              }
            : item
        ),
      }
    : categories;
};

export const scrollRef = nav => {
  if (nav && nav.current && nav.current.scrollBy) {
    nav.current.scrollBy(0, -1000);
  }
};

function Navigation({ mobileNavShown, doSeparateNavigation }) {
  const nav = useRef();

  const { categories: rootCategories, features } = useContext(
    EnvVarsFeaturesContext
  );
  const [categories, setCategories] = useState({ ...rootCategories });
  const isNavigationApi = isMonetateGroup(features, 'navigation-service');

  const fetchDeepCategories = async id => {
    const fetchedChildren = await fetchChildCategories({
      categories,
      id,
      depth: 4,
      isNavigationApi,
    });
    setCategories(fetchedChildren);
  };

  const scrollToTop = useCallback(() => scrollRef(nav));
  const navigateToCategory = useCallback(id => fetchDeepCategories(id));

  if (doSeparateNavigation)
    return (
      <SeparateNavigation
        mobileNavShown={mobileNavShown}
        categories={categories}
        isNavigationApi={isNavigationApi}
      />
    );

  return (
    <nav
      ref={nav}
      className={classNames(styles.container, {
        [styles.open]: mobileNavShown,
      })}
    >
      <div className={styles.capture} />
      <NavigationPane
        id="menu"
        name="Shop by department"
        child={categories.child}
        fetchAndStoreChildCategories={navigateToCategory}
        visible={mobileNavShown}
        scrollToTop={scrollToTop}
      />
      <div className={styles.overlay} />
    </nav>
  );
}

function SeparateNavigation({ mobileNavShown, categories, isNavigationApi }) {
  const menus = [
    {
      name: 'Shop by department',
      child: categories.child,
    },
  ];

  const fetch = ({ id, depth = 2 }) =>
    isNavigationApi
      ? getNavigation({ id, depth })
      : getCategories({ id, depth });

  return (
    <>
      {mobileNavShown ? (
        <MobileMenu menus={menus} fetchChildCategories={fetch} />
      ) : (
        <DesktopMenu child={categories.child} />
      )}
    </>
  );
}

const itemShape = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

SeparateNavigation.propTypes = {
  mobileNavShown: PropTypes.bool.isRequired,
  categories: PropTypes.shape({
    child: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
  }).isRequired,
};

export default Navigation;
